import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowRight, Clock, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { DealCard } from "@/components/DealCard";
import agentChatMockup from "@/assets/agent-chat-mockup.png";
import { optimizeImageUrl } from "@/lib/imageUtils";

interface HomeHeroProps {
  user: any;
  profile: any;
  favoriteProducts: any[];
  recommendedProducts: any[];
}

export const HomeHero = ({ user, profile, favoriteProducts, recommendedProducts }: HomeHeroProps) => {
  // Get user's first name from profile or email
  const getUserFirstName = () => {
    let name = '';
    if (profile?.full_name) {
      name = profile.full_name.split(' ')[0];
    } else if (user?.email) {
      name = user.email.split('@')[0];
    }
    return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
  };

  if (!user) {
    return (
      <section className="bg-white pt-2 md:pt-3 lg:pt-4 pb-6 lg:pb-0">
        <div className="container mx-auto px-4 lg:px-8 max-w-[1600px]">
          <div className="grid lg:grid-cols-[1fr,1fr] gap-6 md:gap-8 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-4 md:space-y-6 text-center lg:text-left lg:pl-8">
              <h1 className="md:text-3xl lg:text-4xl font-bold leading-tight text-foreground text-lg">
                Everything Agents Need — All in One Marketplace
              </h1>
              <p className="hidden md:block text-sm md:text-base leading-relaxed text-muted-foreground">
                From CRMs to yard signs, Circle brings every trusted vendor, product, and service into one verified marketplace. Discover what top agents actually use — and shop smarter, safer, and faster with AI-powered guidance.
              </p>
              
              {/* Mobile-only phone mockup */}
              <div className="block md:hidden my-8 -mx-4">
                <img 
                  src={optimizeImageUrl(agentChatMockup, { width: 800 }) || agentChatMockup} 
                  alt="Agent testimonials and chat interface" 
                  className="w-full" 
                  loading="lazy" 
                />
              </div>

              <div className="flex flex-col items-center sm:items-start gap-4">
                <Link to="/browse" className="flex justify-center">
                  <Button size="lg" className="font-semibold text-sm sm:text-base px-6 py-4 sm:px-8 sm:py-6 bg-primary-button hover:bg-primary-button/90">
                    Shop Agent Tools <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </Link>
                
                {/* AskCircle Secondary CTA */}
                <Link to="/ask-circle" className="text-emerald-700 hover:text-emerald-800 transition-colors text-sm font-medium flex items-center gap-1 group">
                  <span>Not sure what to buy?</span>
                  <span className="flex items-baseline">
                    <span>Ask</span>
                    <span className="font-handelson font-light text-base -ml-0.5">Circle</span>
                  </span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>

                <div className="space-y-1 text-left">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Compare for the first time instantly</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CheckCircle className="h-4 w-4" />
                    <span>No credit card required</span>
                  </div>
                </div>
              </div>
            </div>
          
            {/* Right Column - Hero Phone Image */}
            <div className="hidden lg:flex justify-center items-end h-full">
              <img 
                src="/hero-phone-chat.png" 
                alt="Agent chat interface" 
                className="w-full max-h-[580px] object-contain" 
                fetchPriority="high" 
                loading="eager" 
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Authenticated Hero
  return (
    <section className="bg-white py-6 md:py-12 lg:py-16">
      <div className="container mx-auto px-4 lg:px-8 max-w-[1600px]">
        <div className="space-y-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Welcome back{getUserFirstName() ? `, ${getUserFirstName()}` : ''}! 👋
            </h1>
            <p className="text-muted-foreground">
              Discover new tools and manage your favorites
            </p>
          </div>

          {/* Your Favorites Section */}
          {favoriteProducts.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-foreground">Your Favorites</h2>
                <Link to="/account/favorites">
                  <Button variant="outline" size="sm">
                    View All <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: false,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {favoriteProducts.slice(0, 6).map((product) => (
                    <CarouselItem key={product.id} className="pl-2 md:pl-4 basis-[70%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                      <DealCard {...product} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex left-0 h-10 w-10 bg-background border-2 hover:bg-secondary disabled:opacity-50" />
                <CarouselNext className="hidden md:flex right-0 h-10 w-10 bg-background border-2 hover:bg-secondary disabled:opacity-50" />
              </Carousel>
            </div>
          )}

          {/* Recommended for You Section */}
          {recommendedProducts.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">Recommended for You</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {recommendedProducts.map((product) => (
                  <DealCard key={product.id} {...product} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};